import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { api } from '../lib/api';
import { useAction, useResource } from '../lib/hooks';
import { useSession } from '../lib/session';
import type { GitHubConnection } from '../lib/types';
import { highlight } from './code';
import { BILLING_PATH, Callout, CopyButton, ErrorCallout, InlineConfirm, Spinner } from './ui';

/** All a workflow changes to run a job on offstage. */
const SNIPPET = `jobs:
  test:
    runs-on: offstage-macos`;

/** What the setup route's `?github_error=` codes mean, in one line each. */
const SETUP_ERRORS: Record<string, string> = {
  'github-not-configured': 'GitHub Actions isn’t set up on this plane.',
  'signed-out': 'You were signed out before GitHub sent you back. Sign in and connect again.',
  'owner-only': 'Only an owner of this team can connect GitHub.',
  'install-requested': 'GitHub asked an owner of the organization to approve offstage. Once they have, connect again.',
  'bad-state': 'That GitHub sign-in didn’t start from this browser. Connect again from here.',
  'authorization-needed': 'GitHub didn’t confirm who you are. Connect again, and approve offstage when GitHub asks.',
  'not-org-admin': 'Only an owner of the GitHub organization can connect it, as an owner of this team, from this page.',
  'state-expired': 'That GitHub link expired. Connect again.',
  'wrong-team': 'That GitHub link was for another team or person. Connect again from this team.',
  'missing-installation': 'GitHub didn’t say which installation it made. Connect again.',
  'no-such-installation': 'GitHub doesn’t know that installation any more. Connect again.',
  'github-error': 'GitHub didn’t answer as expected. Try again in a minute.',
  'github-unreachable': 'GitHub didn’t answer. Try again in a minute.',
  'installation-linked-elsewhere': 'That GitHub organization is connected to another offstage team. Disconnect it there first.',
};

/** Refusals a card or a spending limit fixes. */
const BILLING_CODES = new Set(['payment-method-required', 'payment-failed', 'credit-ceiling', 'budget-exceeded']);

const period = (text: string) => (/[.!?…)]$/.test(text.trim()) ? text.trim() : `${text.trim()}.`);

/** GitHub's redirect back lands here with `?connected=` or `?github_error=`: shown once, then dropped from the URL. */
function useSetupNotice(): { tone: 'ok' | 'error'; text: string } | null {
  const [params, setParams] = useSearchParams();
  const [notice] = useState(() => {
    const connected = params.get('connected');
    const failed = params.get('github_error');
    if (connected) return { tone: 'ok' as const, text: `Connected to ${connected}.` };
    if (failed) return { tone: 'error' as const, text: SETUP_ERRORS[failed] ?? `Connecting GitHub didn’t work (${failed}). Try again.` };
    return null;
  });
  useEffect(() => {
    if (!params.has('connected') && !params.has('github_error')) return;
    const next = new URLSearchParams(params);
    next.delete('connected');
    next.delete('github_error');
    setParams(next, { replace: true });
  }, [params, setParams]);
  return notice;
}

/**
 * The top of the CI page: connect a GitHub organization, and the lines a
 * workflow changes. Hidden when the plane has no GitHub app.
 */
export function GitHubCard() {
  const { me } = useSession();
  const isOwner = me?.role === 'owner';
  const notice = useSetupNotice();
  const res = useResource<GitHubConnection>('/v1/github', { interval: 30_000 });
  const connect = useAction(async () => {
    const { url } = await api<{ url: string }>('/v1/github/install', { method: 'POST' });
    window.location.assign(url);
    return url;
  });
  const disconnect = useAction(async (installationId: number) => {
    await api(`/v1/github/installations/${installationId}`, { method: 'DELETE' });
    await res.reload();
    return true;
  });

  const gh = res.data;
  if (!gh?.configured) return null;
  const links = gh.installations;
  const connected = links.some((l) => l.supported);
  const refusal = gh.lastRefusal;

  return (
    <section className="band gh" aria-label="GitHub Actions">
      {notice ? (
        <div className="gh__line">
          <Callout tone={notice.tone} role="status" title={notice.text} />
        </div>
      ) : null}
      {refusal ? (
        <div className="gh__line">
          <Callout
            tone="warn"
            role="status"
            title={`A GitHub job is waiting: ${period(refusal.message)}`}
            actions={
              BILLING_CODES.has(refusal.code) ? (
                <Link className="btn btn--sm btn--primary" to={refusal.code === 'budget-exceeded' ? `${BILLING_PATH}#limit` : BILLING_PATH}>
                  Billing
                </Link>
              ) : undefined
            }
          />
        </div>
      ) : null}
      {connect.error || disconnect.error ? (
        <div className="gh__line">
          <ErrorCallout error={(connect.error ?? disconnect.error)!} />
        </div>
      ) : null}
      <div className="gh__card">
        <div className="gh__text">
          {connected ? null : (
            <>
              <h2 className="h2">Run GitHub Actions on offstage Macs</h2>
              <p>Connect a GitHub organization you own, and each job with these lines runs on a fresh Mac, billed per second.</p>
            </>
          )}
          {links.map((link) => (
            <div key={link.installationId} className="gh__link">
              <span className="gh__account">
                Connected to <strong>{link.account}</strong>
                {link.supported ? null : <span className="cell-sub">{link.reason ? period(link.reason) : 'Only organization repositories run for now.'}</span>}
              </span>
              {isOwner ? (
                <InlineConfirm
                  trigger="Disconnect"
                  question={`Stop running ${link.account}’s jobs?`}
                  confirmLabel="Disconnect"
                  busy={disconnect.busy}
                  onConfirm={() => disconnect.run(link.installationId)}
                />
              ) : null}
            </div>
          ))}
          {connected ? null : isOwner ? (
            <div>
              <button type="button" className="btn btn--primary" aria-busy={connect.busy} disabled={connect.busy} onClick={() => void connect.run()}>
                {connect.busy ? <Spinner size={13} /> : null}
                Connect GitHub
              </button>
            </div>
          ) : (
            <p className="small">An owner of this team connects GitHub.</p>
          )}
        </div>
        <div className="panel panel--plain gh__snippet">
          <pre>{highlight(SNIPPET, 'yaml')}</pre>
          <CopyButton text={SNIPPET} label="Copy the runs-on lines" />
        </div>
      </div>
    </section>
  );
}
